/**
 * Content Type Manager
 * Admin UI for creating/editing content types and managing content items
 */

import React, { useState, useCallback } from 'react';
import {
  Plus,
  Save,
  Trash2,
  Edit3,
  ChevronDown,
  ChevronRight,
  Database,
  FileText,
  GripVertical,
  X,
  Search,
  MoreHorizontal,
  Archive,
  Eye,
} from 'lucide-react';
import { ContentItemEditor } from './ContentItemEditor';
import type {
  ContentType,
  ContentItem,
  ContentField,
  ContentFieldType,
} from '../../types';

// ============================================================================
// TYPES
// ============================================================================

interface ContentTypeManagerProps {
  onApiCall?: (method: string, path: string, body?: any) => Promise<any>;
}

// Available field types for content type builder
const FIELD_TYPES: { value: ContentFieldType; label: string; icon: string }[] = [
  { value: 'text', label: 'Text', icon: 'Aa' },
  { value: 'richtext', label: 'Rich Text', icon: '¶' },
  { value: 'number', label: 'Number', icon: '#' },
  { value: 'boolean', label: 'Boolean', icon: '☑' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'datetime', label: 'Date & Time', icon: '🕐' },
  { value: 'image', label: 'Image', icon: '🖼' },
  { value: 'file', label: 'File', icon: '📎' },
  { value: 'url', label: 'URL', icon: '🔗' },
  { value: 'email', label: 'Email', icon: '✉' },
  { value: 'color', label: 'Color', icon: '🎨' },
  { value: 'select', label: 'Select', icon: '▾' },
  { value: 'multi_select', label: 'Multi-Select', icon: '☰' },
  { value: 'reference', label: 'Reference', icon: '→' },
  { value: 'multi_reference', label: 'Multi-Reference', icon: '⇉' },
  { value: 'json', label: 'JSON', icon: '{}' },
];

// ============================================================================
// FIELD BUILDER
// ============================================================================

interface FieldBuilderProps {
  fields: ContentField[];
  onChange: (fields: ContentField[]) => void;
}

function FieldBuilder({ fields, onChange }: FieldBuilderProps) {
  const [expandedFieldId, setExpandedFieldId] = useState<string | null>(null);

  const addField = () => {
    const newField: ContentField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      name: '',
      label: '',
      type: 'text',
      required: false,
    };
    onChange([...fields, newField]);
    setExpandedFieldId(newField.id);
  };

  const updateField = (index: number, updates: Partial<ContentField>) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], ...updates };

    // Auto-generate name from label if name is empty
    if (updates.label && !updated[index].name) {
      updated[index].name = updates.label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
    }

    onChange(updated);
  };

  const removeField = (index: number) => {
    onChange(fields.filter((_, i) => i !== index));
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;
    const updated = [...fields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      {fields.map((field, index) => {
        const isExpanded = expandedFieldId === field.id;
        return (
          <div key={field.id} className="border rounded-lg">
            <div
              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
              onClick={() => setExpandedFieldId(isExpanded ? null : field.id)}
            >
              <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm font-mono text-gray-500 w-6">
                {FIELD_TYPES.find(t => t.value === field.type)?.icon || '?'}
              </span>
              <span className="text-sm font-medium flex-1">
                {field.label || 'Untitled Field'}
              </span>
              <span className="text-xs text-gray-400">{field.type}</span>
              {field.required && (
                <span className="text-xs text-red-500">Required</span>
              )}
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
            </div>

            {isExpanded && (
              <div className="border-t px-3 py-3 space-y-3 bg-gray-50">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateField(index, { label: e.target.value })}
                      placeholder="Field label"
                      className="w-full border rounded px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Name (API)</label>
                    <input
                      type="text"
                      value={field.name}
                      onChange={(e) => updateField(index, { name: e.target.value })}
                      placeholder="field_name"
                      className="w-full border rounded px-2 py-1.5 text-sm font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                    <select
                      value={field.type}
                      onChange={(e) => updateField(index, { type: e.target.value as ContentFieldType })}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                    >
                      {FIELD_TYPES.map(ft => (
                        <option key={ft.value} value={ft.value}>
                          {ft.icon} {ft.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end gap-3 pb-1">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(index, { required: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">Required</span>
                    </label>
                  </div>
                </div>

                {/* Options for select/multi_select */}
                {(field.type === 'select' || field.type === 'multi_select') && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Options</label>
                    <div className="space-y-1">
                      {(field.options || []).map((opt, optIndex) => (
                        <div key={optIndex} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={opt.label}
                            onChange={(e) => {
                              const newOptions = [...(field.options || [])];
                              newOptions[optIndex] = { ...newOptions[optIndex], label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, '_') };
                              updateField(index, { options: newOptions });
                            }}
                            placeholder="Option label"
                            className="flex-1 border rounded px-2 py-1 text-sm"
                          />
                          <button
                            onClick={() => {
                              const newOptions = (field.options || []).filter((_, i) => i !== optIndex);
                              updateField(index, { options: newOptions });
                            }}
                            className="p-1 hover:bg-red-100 rounded text-red-500"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        const newOptions = [...(field.options || []), { label: '', value: '' }];
                        updateField(index, { options: newOptions });
                      }}
                      className="mt-1 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      <Plus className="h-3 w-3" /> Add Option
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => moveField(index, 'up')}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      title="Move up"
                    >
                      <ChevronDown className="h-4 w-4 rotate-180" />
                    </button>
                    <button
                      onClick={() => moveField(index, 'down')}
                      disabled={index === fields.length - 1}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      title="Move down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeField(index)}
                    className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <button
        onClick={addField}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
      >
        <Plus className="h-4 w-4" />
        Add Field
      </button>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ContentTypeManager({ onApiCall }: ContentTypeManagerProps) {
  // State
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [items, setItems] = useState<ContentItem[]>([]);
  const [editingType, setEditingType] = useState<Partial<ContentType> | null>(null);
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState<'types' | 'items' | 'type-editor' | 'item-editor'>('types');

  const selectedType = contentTypes.find(ct => ct.id === selectedTypeId);

  // Create new content type
  const createNewType = () => {
    setEditingType({
      typeName: '',
      typeSlug: '',
      description: '',
      icon: '📄',
      fields: [],
      singleton: false,
      displayField: 'title',
    });
    setView('type-editor');
  };

  // Save content type
  const saveContentType = useCallback(async () => {
    if (!editingType || !editingType.typeName) return;

    const slug = editingType.typeSlug || editingType.typeName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');

    const payload = {
      type_name: editingType.typeName,
      type_slug: slug,
      description: editingType.description,
      icon: editingType.icon,
      fields: editingType.fields,
      singleton: editingType.singleton,
      display_field: editingType.displayField || 'title',
    };

    if (onApiCall) {
      try {
        if (editingType.id) {
          await onApiCall('PATCH', `/content-types/${editingType.id}`, payload);
        } else {
          await onApiCall('POST', '/content-types', payload);
        }
      } catch (err) {
        console.error('Failed to save content type:', err);
      }
    }

    // Optimistic update
    const savedType: ContentType = {
      id: editingType.id || `temp_${Date.now()}`,
      typeName: editingType.typeName!,
      typeSlug: slug,
      description: editingType.description,
      icon: editingType.icon,
      fields: editingType.fields || [],
      singleton: editingType.singleton || false,
      displayField: editingType.displayField || 'title',
      sortFields: [],
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setContentTypes(prev => {
      const existing = prev.findIndex(ct => ct.id === savedType.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = savedType;
        return updated;
      }
      return [...prev, savedType];
    });

    setEditingType(null);
    setView('types');
  }, [editingType, onApiCall]);

  // Delete content type
  const deleteContentType = useCallback(async (typeId: string) => {
    if (onApiCall) {
      try {
        await onApiCall('DELETE', `/content-types/${typeId}`);
      } catch (err) {
        console.error('Failed to delete content type:', err);
      }
    }
    setContentTypes(prev => prev.filter(ct => ct.id !== typeId));
    if (selectedTypeId === typeId) {
      setSelectedTypeId(null);
      setView('types');
    }
  }, [onApiCall, selectedTypeId]);

  // Select a content type and show its items
  const selectType = (typeId: string) => {
    setSelectedTypeId(typeId);
    setView('items');
    // In real app, fetch items from API here
  };

  // Create new content item
  const createNewItem = () => {
    if (!selectedType) return;
    setEditingItem({
      id: '',
      contentTypeId: selectedType.id,
      data: {},
      status: 'draft',
      sortOrder: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setView('item-editor');
  };

  // Save content item
  const saveItem = useCallback(async (data: Record<string, any>) => {
    if (!selectedType) return;

    if (onApiCall) {
      try {
        if (editingItem?.id) {
          await onApiCall('PATCH', `/content-items/${editingItem.id}`, { data });
        } else {
          await onApiCall('POST', `/content-types/${selectedType.id}/items`, { data, status: 'draft' });
        }
      } catch (err) {
        console.error('Failed to save content item:', err);
      }
    }

    // Optimistic update
    const savedItem: ContentItem = {
      id: editingItem?.id || `temp_${Date.now()}`,
      contentTypeId: selectedType.id,
      data,
      status: editingItem?.status || 'draft',
      sortOrder: editingItem?.sortOrder || 0,
      createdAt: editingItem?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setItems(prev => {
      const existing = prev.findIndex(i => i.id === savedItem.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = savedItem;
        return updated;
      }
      return [...prev, savedItem];
    });

    setEditingItem(null);
    setView('items');
  }, [selectedType, editingItem, onApiCall]);

  // Filter items
  const filteredItems = searchQuery
    ? items.filter(item =>
        JSON.stringify(item.data).toLowerCase().includes(searchQuery.toLowerCase())
      )
    : items;

  // ========================================================================
  // RENDER
  // ========================================================================

  // Type Editor View
  if (view === 'type-editor' && editingType) {
    return (
      <div className="h-full flex flex-col bg-white">
        <div className="border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setEditingType(null); setView('types'); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← Back
            </button>
            <h2 className="text-lg font-semibold">
              {editingType.id ? 'Edit Content Type' : 'New Content Type'}
            </h2>
          </div>
          <button
            onClick={saveContentType}
            disabled={!editingType.typeName}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            Save
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                  <input
                    type="text"
                    value={editingType.icon || ''}
                    onChange={(e) => setEditingType({ ...editingType, icon: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-2xl text-center"
                    maxLength={2}
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editingType.typeName || ''}
                    onChange={(e) => setEditingType({
                      ...editingType,
                      typeName: e.target.value,
                      typeSlug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
                    })}
                    placeholder="e.g., Blog Post"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <input
                    type="text"
                    value={editingType.typeSlug || ''}
                    onChange={(e) => setEditingType({ ...editingType, typeSlug: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingType.description || ''}
                  onChange={(e) => setEditingType({ ...editingType, description: e.target.value })}
                  placeholder="Describe this content type..."
                  className="w-full border rounded-lg px-3 py-2 text-sm h-20"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingType.singleton || false}
                    onChange={(e) => setEditingType({ ...editingType, singleton: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Singleton (only one item)</span>
                </label>
              </div>
            </div>

            {/* Fields */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Fields</h3>
              <FieldBuilder
                fields={(editingType.fields as ContentField[]) || []}
                onChange={(fields) => setEditingType({ ...editingType, fields })}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Item Editor View
  if (view === 'item-editor' && editingItem && selectedType) {
    return (
      <div className="h-full flex flex-col bg-white">
        <div className="border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setEditingItem(null); setView('items'); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← Back to {selectedType.typeName}
            </button>
            <h2 className="text-lg font-semibold">
              {editingItem.id ? 'Edit Item' : 'New Item'}
            </h2>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <ContentItemEditor
            contentType={selectedType}
            item={editingItem}
            onSave={saveItem}
            onCancel={() => { setEditingItem(null); setView('items'); }}
          />
        </div>
      </div>
    );
  }

  // Items List View
  if (view === 'items' && selectedType) {
    return (
      <div className="h-full flex flex-col bg-white">
        <div className="border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setSelectedTypeId(null); setView('types'); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← Content Types
            </button>
            <span className="text-xl">{selectedType.icon}</span>
            <h2 className="text-lg font-semibold">{selectedType.typeName}</h2>
            <span className="text-sm text-gray-500">
              {filteredItems.length} items
            </span>
          </div>
          <button
            onClick={createNewItem}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            New {selectedType.typeName}
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search items..."
              className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Items Table */}
        <div className="flex-1 overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">No items yet</p>
              <button
                onClick={createNewItem}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                Create First {selectedType.typeName}
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">
                    {selectedType.displayField || 'Title'}
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">
                    Status
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">
                    Updated
                  </th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredItems.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => { setEditingItem(item); setView('item-editor'); }}
                  >
                    <td className="px-4 py-3 text-sm">
                      {item.data[selectedType.displayField || 'title'] || '(Untitled)'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${
                        item.status === 'published'
                          ? 'bg-green-100 text-green-700'
                          : item.status === 'archived'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(item.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button className="p-1 hover:bg-gray-100 rounded">
                        <MoreHorizontal className="h-4 w-4 text-gray-400" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // Content Types List (default)
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold">Content Types</h2>
        </div>
        <button
          onClick={createNewType}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          New Content Type
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {contentTypes.length === 0 ? (
          <div className="text-center py-16">
            <Database className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Content Types</h3>
            <p className="text-gray-500 mb-4">
              Create content types to define custom data structures for your site.
            </p>
            <button
              onClick={createNewType}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              Create Your First Content Type
            </button>
          </div>
        ) : (
          <div className="grid gap-3">
            {contentTypes.map((ct) => (
              <div
                key={ct.id}
                className="flex items-center gap-4 p-4 border rounded-lg hover:border-blue-300 cursor-pointer transition-colors"
                onClick={() => selectType(ct.id)}
              >
                <span className="text-2xl">{ct.icon || '📄'}</span>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{ct.typeName}</h3>
                  <p className="text-sm text-gray-500">
                    {ct.description || `${ct.fields.length} fields`}
                    {ct.singleton && ' · Singleton'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingType(ct);
                      setView('type-editor');
                    }}
                    className="p-2 hover:bg-gray-100 rounded"
                    title="Edit type"
                  >
                    <Edit3 className="h-4 w-4 text-gray-500" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`Delete "${ct.typeName}"? This will also delete all its items.`)) {
                        deleteContentType(ct.id);
                      }
                    }}
                    className="p-2 hover:bg-red-100 rounded"
                    title="Delete type"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContentTypeManager;
